import { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiPause, FiPlay } from 'react-icons/fi';
import { activeHighlights, getHighlights } from '../../services/highlights';
import EditorialImage from './EditorialImage';
import EditorialLink from './EditorialLink';

export default function WeeklyHighlights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 700px)').matches);
  const [visible, setVisible] = useState(false);
  const section = useRef(null);
  const rail = useRef(null);
  const active = activeHighlights(items, clock);
  const current = active.length ? index % active.length : 0;
  const displayed = compact ? active : [...active.slice(current), ...active.slice(0, current)];

  useEffect(() => {
    const media = window.matchMedia('(max-width: 700px)');
    const changed = () => { setCompact(media.matches); setIndex(0); rail.current?.scrollTo({left: 0}); };
    media.addEventListener('change', changed);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {threshold: .2});
    observer.observe(section.current);
    return () => { media.removeEventListener('change', changed); observer.disconnect(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const refresh = () => getHighlights(controller.signal).then(data => { if (!controller.signal.aborted) setItems(data); }).catch(() => {}).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    refresh();
    const poll = setInterval(() => { if (!document.hidden) refresh(); }, 60000);
    const timer = setInterval(() => setClock(Date.now()), 30000);
    return () => { controller.abort(); clearInterval(timer); clearInterval(poll); };
  }, []);

  const move = (next, automatic = false) => {
    if (!active.length) return;
    const value = (next + active.length) % active.length;
    setIndex(value);
    if (compact) {
      const item = rail.current?.children[value];
      rail.current?.scrollTo({ left: item?.offsetLeft - rail.current.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    if (!automatic) setPaused(true);
  };

  useEffect(() => {
    if (paused || !visible || active.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => {
      if (!document.hidden) move(current + 1, true);
    }, 5000);
    return () => clearInterval(timer);
    // move is intentionally refreshed when the active index or set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, paused, active.length, compact, visible]);

  return <section className="hlt-highlights" ref={section} aria-labelledby="highlights-title">
    <div className="hlt-section-heading">
      <div><h2 id="highlights-title">This week's highlights</h2><p>Curated experiences for what's next</p></div>
      {!!active.length && <div className="hlt-controls">
        <button aria-label={paused ? 'Play highlights' : 'Pause highlights'} title={paused ? 'Play highlights' : 'Pause highlights'} onClick={() => setPaused(!paused)}>{paused ? <FiPlay /> : <FiPause />}</button>
        <button aria-label="Previous highlight" title="Previous highlight" onClick={() => move(index - 1)}><FiChevronLeft /></button>
        <button aria-label="Next highlight" title="Next highlight" onClick={() => move(index + 1)}><FiChevronRight /></button>
        <span>{String(current + 1).padStart(2, '0')} / {String(active.length).padStart(2, '0')}</span>
      </div>}
    </div>
    {loading ? <div className="hlt-highlight-loading" aria-label="Loading highlights" aria-busy="true" /> : active.length ?
      <div className="hlt-highlight-rail" ref={rail} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)} onTouchStart={() => setPaused(true)}
        onScroll={() => { if (window.innerWidth <= 700 && rail.current) setIndex(Math.min(active.length - 1, Math.round(rail.current.scrollLeft / (rail.current.children[0]?.getBoundingClientRect().width || 1)))); }}>
        {displayed.map((item) => <EditorialLink key={item.id} to={item.cta_url} className="hlt-highlight">
          <EditorialImage src={item.desktop_image_url} mobileSrc={item.mobile_image_url} alt={item.image_alt || ''} sizes="(max-width: 700px) 90vw, 25vw" />
          <div className="hlt-highlight-copy"><span className="hlt-sequence">{String(active.indexOf(item) + 1).padStart(2, '0')}</span><span className="hlt-eyebrow">{item.category}</span><h3>{item.title}</h3><p>{item.subtitle}</p></div>
        </EditorialLink>)}
      </div> : <div className="hlt-highlights-empty"><p>Your next experience starts with a conversation.</p><EditorialLink to="/concierge">Speak to concierge</EditorialLink></div>}
  </section>;
}
